import { Button } from '../ui/button';
import {
  Shield, FileText, ScanLine, BarChart3, Upload, Sparkles,
  Clock, BookOpen, Printer, BrainCircuit, Shuffle, CheckCircle,
  Lightbulb, Heart, ArrowRight, GraduationCap, MapPin, Scale
} from 'lucide-react';

const FOUNDER_PHOTO = 'https://customer-assets.emergentagent.com/job_antitriche-hub/artifacts/4twlqxhu_Henri%20GROUES%20portrait.jpg';

// Static marketing sections (problem → footer) below the hero.
export const LandingSections = ({ howRef, openRegister }) => (
  <>
      {/* ═══════ 2. PROBLÈME ═══════ */}
      <section className="relative border-t border-white/[0.04]">
        <div className="max-w-3xl mx-auto px-5 md:px-8 py-12 md:py-28">
          <h2 className="text-2xl sm:text-3xl font-black tracking-tight mb-8 leading-tight">
            Le QCM papier est pratique.<br />
            <span className="text-gray-500">Sa préparation et sa correction prennent trop de temps.</span>
          </h2>
          <div className="space-y-5 text-gray-400 leading-relaxed text-base">
            <p>Créer un questionnaire clair, équilibré et adapté à une classe demande du temps. Il faut rédiger les questions, préparer les réponses, imprimer, distribuer, éviter la triche, puis corriger toutes les copies.</p>
            <p>Quand les classes sont nombreuses, cela devient vite lourd, répétitif et chronophage.</p>
            <p className="text-gray-200 font-medium">QCMAntiTriche a été conçu pour alléger tout ce travail.</p>
          </div>
        </div>
      </section>

      {/* ═══════ 3. SOLUTION ═══════ */}
      <section className="relative border-t border-white/[0.04] bg-[#0a0e16]">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-12 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div>
              <h2 className="text-2xl sm:text-3xl font-black tracking-tight mb-8 leading-tight">
                Un outil simple pour préparer et corriger vos évaluations papier beaucoup plus vite
              </h2>
              <div className="space-y-5 text-gray-400 leading-relaxed text-base">
                <p>Avec QCMAntiTriche, vous pouvez créer vos QCM à la main ou laisser l'IA générer en quelques secondes un questionnaire à partir de vos propres documents de cours.</p>
                <p>Vous choisissez :</p>
                <ul className="space-y-2 pl-1">
                  {['la classe,', 'le niveau de difficulté,', 'le nombre de questions,', 'le nombre de réponses par question.'].map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <p>L'outil vous permet ensuite d'imprimer vos questionnaires, d'attribuer à chaque élève un QCM personnalisé avec les mêmes questions dans un ordre aléatoire, puis de corriger automatiquement les copies après un scan.</p>
              </div>
            </div>
            <div className="flex justify-center">
              <img
                src="https://customer-assets.emergentagent.com/job_antitriche-hub/artifacts/un7skjm7_antitriche-hub.preview.emergentagent.com_dashboard%20%282%29.png"
                alt="Interface de création de QCM"
                className="w-full rounded-2xl border border-white/[0.06] shadow-xl shadow-black/40"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ 4. COMMENT ÇA FONCTIONNE ═══════ */}
      <section ref={howRef} className="relative border-t border-white/[0.04]" id="how">
        <div className="max-w-5xl mx-auto px-5 md:px-8 py-12 md:py-28">
          <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-center mb-16">Comment ça fonctionne</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { num: '1', icon: GraduationCap, title: 'Choisissez votre classe et votre niveau', desc: 'Indiquez la classe concernée, le niveau de difficulté souhaité et le format de votre questionnaire.' },
              { num: '2', icon: BrainCircuit, title: 'Générez votre QCM en quelques secondes', desc: "L'IA crée des questions à partir de vos propres supports de cours, prêtes à être vérifiées, modifiées et imprimées." },
              { num: '3', icon: Printer, title: 'Faites passer votre contrôle sur papier', desc: 'Chaque élève reçoit un QCM personnalisé avec les mêmes questions, mais dans un ordre aléatoire, afin de limiter les risques de copie.' },
              { num: '4', icon: ScanLine, title: 'Scannez les copies et récupérez les notes', desc: "L'outil détecte automatiquement les réponses et calcule les résultats sans correction manuelle." },
            ].map((step) => (
              <div key={step.num} className="relative">
                <div className="w-10 h-10 rounded-xl bg-blue-600/10 border border-blue-500/20 flex items-center justify-center mb-5">
                  <step.icon className="w-5 h-5 text-blue-400" strokeWidth={1.5} />
                </div>
                <p className="text-xs font-bold text-blue-500 uppercase tracking-widest mb-2">Étape {step.num}</p>
                <h3 className="text-base font-bold mb-2 leading-snug">{step.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ 5. BÉNÉFICES ═══════ */}
      <section className="relative border-t border-white/[0.04] bg-[#0a0e16]">
        <div className="max-w-5xl mx-auto px-5 md:px-8 py-12 md:py-28">
          <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-center mb-16">Ce que vous gagnez concrètement</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Clock, title: 'Un vrai gain de temps', desc: 'Plus besoin de repartir de zéro pour créer chaque questionnaire.' },
              { icon: BookOpen, title: 'Des QCM adaptés à votre cours', desc: "Les questions sont générées à partir de vos propres documents, pas à partir d'un contenu générique." },
              { icon: Shuffle, title: 'Moins de triche en classe', desc: 'Chaque élève reçoit un QCM personnalisé avec les mêmes questions dans un ordre différent.' },
              { icon: CheckCircle, title: 'Une correction automatique', desc: 'Après le scan des copies, les réponses sont détectées et les notes calculées automatiquement.' },
              { icon: Lightbulb, title: 'Moins de charge mentale', desc: "Vous passez moins de temps sur la logistique et plus de temps sur l'essentiel." },
              { icon: Heart, title: 'Une prise en main simple', desc: "L'outil a été pensé pour être utilisé rapidement, sans complexité inutile." },
            ].map((b) => (
              <div key={b.title} className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.05] hover:border-blue-500/20 transition-colors">
                <b.icon className="w-5 h-5 text-blue-400 mb-4" strokeWidth={1.5} />
                <h3 className="text-sm font-bold mb-2">{b.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ 6. FONCTIONNALITÉS ═══════ */}
      <section className="relative border-t border-white/[0.04]">
        <div className="max-w-5xl mx-auto px-5 md:px-8 py-12 md:py-28">
          <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-center mb-16">Tout ce qu'il vous faut pour créer et corriger vos QCM papier</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Sparkles, title: 'Génération par IA à partir de vos documents', desc: 'Créez un questionnaire en quelques secondes depuis vos supports de cours.' },
              { icon: GraduationCap, title: 'QCM adaptés à votre classe', desc: 'Ajustez la difficulté, la classe et le format selon votre objectif pédagogique.' },
              { icon: Shuffle, title: 'QCM personnalisés par élève', desc: "Les mêmes questions sont conservées, mais leur ordre est mélangé pour limiter la copie." },
              { icon: ScanLine, title: 'Correction automatique', desc: "Scannez les copies et laissez l'outil corriger les réponses." },
              { icon: BarChart3, title: 'Notes immédiates', desc: 'Obtenez les résultats sans ressaisie manuelle.' },
              { icon: Upload, title: "Import de listes d'élèves", desc: "Gagnez du temps dans la préparation de vos évaluations." },
            ].map((f) => (
              <div key={f.title} className="group p-6 rounded-2xl border border-white/[0.05] hover:border-blue-500/20 transition-all hover:bg-white/[0.01]">
                <div className="w-10 h-10 rounded-xl bg-blue-600/10 border border-blue-500/20 flex items-center justify-center mb-4 group-hover:bg-blue-600/15 transition-colors">
                  <f.icon className="w-5 h-5 text-blue-400" strokeWidth={1.5} />
                </div>
                <h3 className="text-sm font-bold mb-2">{f.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ 7. CIBLE ═══════ */}
      <section className="relative border-t border-white/[0.04] bg-[#0a0e16]">
        <div className="max-w-3xl mx-auto px-5 md:px-8 py-12 md:py-28 text-center">
          <h2 className="text-2xl sm:text-3xl font-black tracking-tight mb-8 leading-tight">
            Conçu pour les enseignants qui veulent garder le papier sans garder la charge qui va avec
          </h2>
          <p className="text-base text-gray-400 leading-relaxed max-w-2xl mx-auto">
            QCMAntiTriche s'adresse aux enseignants du collège, du lycée et du post-bac qui utilisent le papier en classe parce qu'il reste simple, rassurant et efficace, mais qui ne veulent plus passer des heures à préparer et corriger leurs QCM.
          </p>
        </div>
      </section>

      {/* ═══════ 8. FONDATEUR ═══════ */}
      <section className="relative border-t border-white/[0.04]">
        <div className="max-w-3xl mx-auto px-5 md:px-8 py-12 md:py-28">
          <h2 className="text-2xl sm:text-3xl font-black tracking-tight mb-10">Pourquoi j'ai créé QCMAntiTriche</h2>
          <div className="flex flex-col md:flex-row gap-8 items-start">
            <img
              src={FOUNDER_PHOTO}
              alt="Henri GROUÈS"
              className="w-28 h-28 md:w-32 md:h-32 rounded-full object-cover object-top shrink-0 border-2 border-white/[0.08]"
            />
            <div className="space-y-4 text-gray-400 leading-relaxed text-[15px]">
              <p>Je suis professeur, et j'ai créé QCMAntiTriche pour répondre à un problème très concret que je vivais moi-même.</p>
              <p>Je voulais continuer à utiliser le format papier en classe, parce qu'il est simple, rapide à mettre en place et confortable pour les élèves. Mais je ne voulais plus perdre autant de temps à créer mes questionnaires, préparer une distribution propre, corriger les copies et ressaisir les notes.</p>
              <p>J'ai donc conçu un outil capable de faire deux choses essentielles :</p>
              <ul className="space-y-2 pl-1">
                <li className="flex items-start gap-3">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" />
                  <span>générer en quelques secondes un QCM à partir de mes propres supports de cours, selon la classe et le niveau attendus ;</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" />
                  <span>corriger automatiquement les copies pour éviter des heures de correction manuelle.</span>
                </li>
              </ul>
              <p>Aujourd'hui, QCMAntiTriche m'aide à aller beaucoup plus vite dans la préparation de mes évaluations, à réduire la charge de correction et à me concentrer davantage sur l'enseignement.</p>
              <div className="pt-2">
                <p className="text-white font-bold">Henri GROUÈS</p>
                <p className="text-sm text-blue-400">Conçu par un professeur, pour des professeurs.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ 9. RÉASSURANCE ═══════ */}
      <section className="relative border-t border-white/[0.04] bg-[#0a0e16]">
        <div className="max-w-3xl mx-auto px-5 md:px-8 py-12 md:py-28">
          <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-center mb-12">Un outil pensé pour un usage réel en classe</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-5 max-w-xl mx-auto">
            {[
              { icon: FileText, text: 'Format papier simple à utiliser' },
              { icon: Sparkles, text: 'Création rapide à partir de vos supports' },
              { icon: CheckCircle, text: 'Correction automatique' },
              { icon: Shuffle, text: 'QCM personnalisés par élève' },
              { icon: MapPin, text: 'Données hébergées en France' },
              { icon: Scale, text: 'Compatible avec la démarche RGPD' },
            ].map((item) => (
              <div key={item.text} className="flex items-center gap-3">
                <item.icon className="w-4 h-4 text-blue-400 shrink-0" strokeWidth={1.5} />
                <span className="text-sm text-gray-300">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ 10. CTA FINAL ═══════ */}
      <section className="relative border-t border-white/[0.04]">
        <div className="max-w-3xl mx-auto px-5 md:px-8 py-12 md:py-28 text-center">
          <h2 className="text-2xl sm:text-3xl font-black tracking-tight mb-5">Gagnez du temps sur vos QCM papier</h2>
          <p className="text-base text-gray-400 mb-10 max-w-lg mx-auto leading-relaxed">
            Créez vos questionnaires à partir de vos documents de cours et corrigez automatiquement vos copies.
          </p>
          <Button data-testid="cta-final-btn" onClick={openRegister} size="lg" className="bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-900/30 text-base px-10 h-12">
            Commencer gratuitement <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      {/* ═══════ FOOTER ═══════ */}
      <footer className="border-t border-white/[0.04] py-8">
        <div className="max-w-6xl mx-auto px-5 md:px-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-gray-600">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-blue-500/50" strokeWidth={1.5} />
            <span>QCMAntiTriche</span>
          </div>
          <p>Données hébergées en France &middot; Compatible avec la démarche RGPD</p>
        </div>
      </footer>
  </>
);
